import type { ThreadTopicHorizon } from '@shared/types/thread-work';
import type { TodayRow } from './today-pipeline';
import { TODAY_STAGES } from './today-pipeline';

/**
 * J5. A suggestion is Domino's proposal nobody took on: it never goes to a
 * backlog and leaves Aujourd'hui once its proposal is no longer active. An
 * engagement is work a person took on (asked for, accepted, launched): it
 * never leaves by itself and lives on one horizon.
 */
export type TodayCommitment = 'engagement' | 'suggestion';
export type TodayBucket = 'today' | 'later' | 'someday';

export interface PlannedTodayRow {
  row: TodayRow;
  commitment: TodayCommitment;
  bucket: TodayBucket;
  /** Only persistent topics can be planned (legacy rows have no Thread topic). */
  plannable: boolean;
  /** Engaged before today and still open: shown with its age, part of the review. */
  rolledOver: boolean;
}

/** Plannable = the row is a persistent Thread topic. */
export const isPlannableRow = (row: TodayRow): boolean =>
  row.kind === 'app' || row.item.subject.threadState?.source === 'persistent';

const isActiveProposal = (row: TodayRow, now: Date): boolean => {
  const proposal = row.topic.proposal;
  if (!proposal || proposal.status !== 'active') return false;
  return !proposal.expiresAt || Date.parse(proposal.expiresAt) > now.getTime();
};

export const getRowCommitment = (row: TodayRow): TodayCommitment => {
  const { topic } = row;
  if (!isPlannableRow(row)) return 'suggestion';
  return topic.committedAt || topic.prioritySource === 'user' || topic.activeActionRun
    || topic.visibleState === 'awaiting_review' || topic.visibleState === 'running'
    ? 'engagement'
    : 'suggestion';
};

const bucketOf = (horizon: ThreadTopicHorizon | null | undefined, plannedFor: string | null | undefined, today: string): TodayBucket =>
  horizon === 'someday' ? 'someday'
  : horizon === 'later' && plannedFor && plannedFor > today ? 'later'
  : 'today';

/** Local calendar date, YYYY-MM-DD. */
export const isoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Engagements keep their horizon; suggestions show only while their proposal is
 * active (legacy rows: while actionable). Nothing is ranked from labels.
 */
export const planTodayRows = (rows: TodayRow[], now: Date): PlannedTodayRow[] => {
  const today = isoDay(now);
  return rows.flatMap((row): PlannedTodayRow[] => {
    const commitment = getRowCommitment(row);
    const plannable = isPlannableRow(row);
    if (commitment === 'suggestion') {
      const visible = plannable ? isActiveProposal(row, now) : row.topic.visibleState === 'actionable';
      return visible ? [{ row, commitment, bucket: 'today', plannable, rolledOver: false }] : [];
    }
    const bucket = bucketOf(row.topic.horizon, row.topic.plannedFor, today);
    const committedDay = row.topic.committedAt ? isoDay(new Date(row.topic.committedAt)) : null;
    const rolledOver = bucket === 'today' && committedDay !== null && committedDay < today;
    return [{ row, commitment, bucket, plannable, rolledOver }];
  });
};

const STAGE_RANK = Object.fromEntries(TODAY_STAGES.map(({ stage }, index) => [stage, index]));

/** Engagements by step (to validate first), then the read model's order. */
export const sortEngagements = (rows: PlannedTodayRow[]): PlannedTodayRow[] =>
  [...rows].sort((left, right) => (STAGE_RANK[left.row.stage] ?? 0) - (STAGE_RANK[right.row.stage] ?? 0));

/** Days since a date, for the age of a rolled-over engagement. */
export const daysSince = (iso: string, now: Date): number =>
  Math.max(0, Math.round((Date.parse(isoDay(now)) - Date.parse(isoDay(new Date(iso)))) / 86_400_000));

/** Review queue: rolled-over engagements, then the backlog. */
export const reviewQueue = (rows: PlannedTodayRow[]): PlannedTodayRow[] => [
  ...rows.filter((item) => item.plannable && item.rolledOver),
  ...rows.filter((item) => item.bucket === 'someday'),
];

/** Quick dates of « Plus tard »: tomorrow and next Monday. */
export const quickDates = (now: Date): { tomorrow: string; monday: string } => {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const daysToMonday = ((8 - now.getDay()) % 7) || 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToMonday);
  return { tomorrow: isoDay(tomorrow), monday: isoDay(monday) };
};

const shortDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** The short data a planned row shows under its state (« Un jour », its day, its age). */
export const plannedRowNote = (item: PlannedTodayRow, now: Date): string | null => {
  if (item.bucket === 'someday') return 'Un jour';
  if (item.bucket === 'later' && item.row.topic.plannedFor) return shortDay(item.row.topic.plannedFor);
  if (item.rolledOver && item.row.topic.committedAt) return `${daysSince(item.row.topic.committedAt, now)} j`;
  return null;
};
